/**
 * Singleton binding seam for the chat composer's MCP-servers integration.
 *
 * The composition root (`main.tsx`) calls `configureChatMcp()` once at app boot with the
 * implementation from `@/features/user-mcp-servers`. The chat composer (`features/chats`) then
 * reads it via `getChatMcp()` WITHOUT importing `@/features/user-mcp-servers` directly.
 *
 * Why: features must NOT cross-import sibling features (FSD layer order, enforced by
 * `features/chats/fsd-boundaries.test.ts`). This seam is the same pattern as
 * `lib/chat-actions/chat-actions-bindings.ts`, but in the reverse direction — here `chats`
 * is the consumer of a capability another feature provides.
 *
 * Unlike chat-actions, MCP is optional: the seam defaults to a no-op so chats renders fine even
 * if the binding is never configured (e.g. in tests). The default returns "no MCPs" and calls no
 * hooks, so the hook-call shape stays consistent as long as `configureChatMcp` runs synchronously
 * at boot (before the first chat render), exactly like the other seams in `main.tsx`.
 */
import type { ReactNode } from 'react';

export interface ChatAgentMcpState {
  /** Whether the agent has any MCP servers assigned — gates the "Connectors" menu entry. */
  hasMcps: boolean;
  /** A user-actionable MCP is not installed or failing — drives the "+"/Connectors warning dot. */
  needsAttention: boolean;
  /** "Connectors" submenu to render inside the composer's + Add menu. */
  connectorsSlot: ReactNode;
  /** Credentials modal to render at composer root (survives the menu unmounting). */
  modalSlot: ReactNode;
}

export interface ChatAgentMcpOptions {
  /** Close the composer's + Add menu (run before opening the auth modal). */
  onCloseMenu: () => void;
}

export interface ChatMcpBindings {
  /** Resolves an agent's MCP servers to live status + composer UI slots. Hook semantics. */
  useChatAgentMcp: (agentId: string | undefined, options: ChatAgentMcpOptions) => ChatAgentMcpState;
}

const NO_MCPS: ChatAgentMcpState = {
  hasMcps: false,
  needsAttention: false,
  connectorsSlot: null,
  modalSlot: null,
};

const noopBindings: ChatMcpBindings = {
  useChatAgentMcp: () => NO_MCPS,
};

let bindings: ChatMcpBindings = noopBindings;

/** Configures the chat-MCP bindings. Called once at app boot from the composition root. */
export const configureChatMcp = (next: ChatMcpBindings): void => {
  bindings = next;
};

/** Test-only reset. Re-applies the bindings or restores the no-op default. */
export const configureChatMcpForTests = (next: ChatMcpBindings | null): void => {
  bindings = next ?? noopBindings;
};

/** Returns the bound chat-MCP helpers (no-op default until `configureChatMcp` runs at boot). */
export const getChatMcp = (): ChatMcpBindings => bindings;
